import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MaterialReactTable } from 'material-react-table';
import diagnosticImagingService from '../../services/diagnosticImagingService'; // Importar el servicio real
import { API_DOCTOR_LIST } from '../../../../helpers/urlHelper';
import { 
  FiSearch, 
  FiSettings, 
  FiPlus, 
  FiChevronDown,
  FiMail,
  FiMessageSquare,
  FiDownload,
  FiX,
  FiCheck,
  FiClock,
  FiPause,
  FiSquare,
  FiAlertTriangle,
  FiFileText,
  FiLock,
  FiEye,
  FiFilter,
  FiInfo,
  FiUser,
  FiCalendar,
  FiTag,
  FiUpload,
  FiFile,
  FiTrash2
} from 'react-icons/fi';

const DiagnosticImagingTreatments = ({ patientId, onCountUpdate, componentName = 'DiagnosticImaging' }) => {
  const [diagnosticStudies, setDiagnosticStudies] = useState([]);
  const [filteredStudies, setFilteredStudies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [showAddOffcanvas, setShowAddOffcanvas] = useState(false);
  const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);
  const [showViewOffcanvas, setShowViewOffcanvas] = useState(false);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [showMostRecentDropdown, setShowMostRecentDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [doctors, setDoctors] = useState([]);
  
  // Filter state
  const [filters, setFilters] = useState({
    type: '',
    bodyPart: '',
    status: 'All',
    priority: 'All',
    lastUpdate: '',
    startDate: '',
    endDate: ''
  });

  // Form state for new diagnostic study - AJUSTADO PARA COINCIDIR CON EL BACKEND
  const [newStudy, setNewStudy] = useState({
    patientId: patientId,
    studyName: '', // Cambiado a studyName para coincidir con el backend
    studyType: '', // Cambiado a studyType para coincidir con el backend
    bodyPart: '',
    status: 'In Progress',
    priority: 'Medium',
    consent: false,
    notes: '',
    scheduledDate: '',
    doctorId: ''
  });

  // Constantes para los enums
  const STUDY_TYPES = [
    'Radiografía',
    'Resonancia Magnética',
    'Tomografía',
    'Ecografía',
    'Mamografía',
    'Densitometría',
    'Angiografía',
    'Endoscopia',
    'Colonoscopia',
    'Electrocardiograma'
  ];

  const BODY_PARTS = [
    'Mano',
    'Pie',
    'Cara',
    'Tórax',
    'Abdomen',
    'Pelvis',
    'Columna',
    'Rodilla',
    'Hombro',
    'Cadera',
    'Cráneo',
    'Corazón',
    'Cuello',
    'Completo'
  ];

  const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];
  const STATUSES = ['In Progress', 'Completed'];

  // Fetch doctors from API
  const fetchDoctors = async () => {
    try {
      const doctorsResponse = await fetch(`${API_DOCTOR_LIST}`);
      if (!doctorsResponse.ok) {
        throw new Error('Failed to fetch doctors');
      }
      const doctorsData = await doctorsResponse.json();
      
      console.log('Raw doctors response:', doctorsData);
      
      const doctorsArray = doctorsData && doctorsData.data ? doctorsData.data : [];
      
      console.log('Processed doctors array:', doctorsArray);
      setDoctors(doctorsArray);
    } catch (err) {
      console.error('Error fetching doctors:', err);
      setDoctors([]);
    }
  };

  const handleDoctorSelection = (doctorId) => {
    console.log('Doctor seleccionado:', doctorId);
    console.log('Lista de doctores disponibles:', doctors);
    
    const selectedDoctor = doctors.find(d => d._id === doctorId);
    console.log('Doctor encontrado:', selectedDoctor);
    
    if (selectedDoctor) {
      setNewStudy(prev => {
        const updated = {
          ...prev,
          doctorId: selectedDoctor._id
        };
        console.log('Estado actualizado newStudy.doctorId:', updated.doctorId);
        return updated;
      });
    } else if (doctorId === '') {
      // Caso cuando se deselecciona el doctor
      setNewStudy(prev => ({
        ...prev,
        doctorId: ''
      }));
      console.log('Doctor deseleccionado, doctorId limpiado');
    } else {
      console.warn('Doctor no encontrado para ID:', doctorId);
    }
  };

  // Enhanced loadDiagnosticStudies function con callback para optimización
  const loadDiagnosticStudies = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log('Loading diagnostic studies for patient:', patientId);
      
      // Use the new specific method for getting diagnostics by patient
      const response = await diagnosticImagingService.getDiagnosticsByPatient(patientId);
      console.log('Load response:', response);
      
      let studiesData = [];
      
      if (response.success && response.data) {
        studiesData = Array.isArray(response.data) ? response.data : [response.data];
      } else if (!response.success) {
        console.error('Failed to load studies:', response.message);
        studiesData = [];
      }

      // Process and normalize each study
      const normalizedStudies = studiesData.map(study => ({
        _id: study._id || study.id,
        name: study.name || study.studyName || 'Estudio sin nombre',
        type: study.type || study.studyType || 'Radiografía',
        bodyPart: study.bodyPart || '',
        status: study.status || 'In Progress',
        lastUpdate: study.updatedAt || study.lastUpdate || new Date(),
        patientId: study.patientId || patientId,
        doctorId: study.doctorId || '',
        files: study.files || [],
        consent: study.consent || false,
        notes: study.notes || '',
        priority: study.priority || 'Medium',
        scheduledDate: study.scheduledDate || null,
        completedDate: study.completedDate || null,
        createdAt: study.createdAt || new Date(),
        updatedAt: study.updatedAt || new Date()
      }));

      console.log('Normalized studies:', normalizedStudies);
      setDiagnosticStudies(normalizedStudies);
      
      // IMPORTANTE: Actualizar contador solo cuando los datos cambien
      if (onCountUpdate && normalizedStudies.length !== diagnosticStudies.length) {
        onCountUpdate(componentName, normalizedStudies.length);
      }
      
    } catch (error) {
      console.error('Error loading diagnostic studies:', error);
      setDiagnosticStudies([]);
      
      // Actualizar contador a 0 en caso de error
      if (onCountUpdate) {
        onCountUpdate(componentName, 0);
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      console.error('Load error details:', errorMessage);
      
      if (window.Swal) {
        window.Swal.fire({
          icon: 'warning',
          title: 'Loading Warning',
          text: `Could not load diagnostic studies: ${errorMessage}`,
          timer: 5000,
          toast: true,
          position: 'top-end',
          showConfirmButton: false
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [patientId, onCountUpdate, componentName, diagnosticStudies.length]);

  // Load diagnostic studies data
  useEffect(() => {
    if (patientId) {
      loadDiagnosticStudies();
      fetchDoctors(); // Cargar doctores al montar el componente
      setNewStudy(prev => ({
        ...prev,
        patientId: patientId
      }));
    } else {
      console.warn('No patientId provided to DiagnosticImagingTreatments');
      setDiagnosticStudies([]);
      // Actualizar contador a 0 si no hay patientId
      if (onCountUpdate) {
        onCountUpdate(componentName, 0);
      }
    }
  }, [patientId, loadDiagnosticStudies]);

  // Efecto separado para actualizar contador cuando diagnosticStudies cambie
  useEffect(() => {
    if (onCountUpdate) {
      onCountUpdate(componentName, diagnosticStudies.length);
    }
  }, [diagnosticStudies.length, onCountUpdate, componentName]);

  // Apply filters whenever studies or filters change
  useEffect(() => {
    applyFilters();
  }, [diagnosticStudies, filters, globalFilter]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setShowMostRecentDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Prevent body scroll when offcanvas are open
  useEffect(() => {
    if (showAddOffcanvas || showViewOffcanvas || showFilterOffcanvas) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [showAddOffcanvas, showViewOffcanvas, showFilterOffcanvas]);

  const applyFilters = () => {
    let filtered = [...diagnosticStudies];

    // Global search filter
    if (globalFilter) {
      filtered = filtered.filter(study => 
        study.name.toLowerCase().includes(globalFilter.toLowerCase()) ||
        study.type.toLowerCase().includes(globalFilter.toLowerCase()) ||
        (study.bodyPart && study.bodyPart.toLowerCase().includes(globalFilter.toLowerCase()))
      );
    }

    // Type filter
    if (filters.type) {
      filtered = filtered.filter(study => study.type === filters.type);
    }

    // Body part filter
    if (filters.bodyPart) {
      filtered = filtered.filter(study => study.bodyPart === filters.bodyPart);
    }

    // Status filter
    if (filters.status !== 'All') {
      filtered = filtered.filter(study => study.status === filters.status);
    }

    // Priority filter
    if (filters.priority !== 'All') {
      filtered = filtered.filter(study => study.priority === filters.priority);
    }

    // Date filters
    if (filters.startDate) {
      filtered = filtered.filter(study => {
        const studyDate = new Date(study.lastUpdate);
        return studyDate >= new Date(filters.startDate);
      });
    }

    if (filters.endDate) {
      filtered = filtered.filter(study => {
        const studyDate = new Date(study.lastUpdate);
        return studyDate <= new Date(filters.endDate);
      });
    }

    // Last update filter
    if (filters.lastUpdate) {
      filtered = filtered.filter(study => {
        const updateDate = new Date(study.lastUpdate).toISOString().split('T')[0];
        return updateDate === filters.lastUpdate;
      });
    }

    setFilteredStudies(filtered);
  };

  const handleGlobalFilterChange = (filter) => {
    setGlobalFilter(filter);
  };

  const handleViewStudy = (studyId) => {
    const study = diagnosticStudies.find(s => s._id === studyId);
    if (study) {
      setSelectedStudy(study);
      setShowViewOffcanvas(true);
    }
  };

  const handleAddNew = () => {
    // Reset form state before opening
    setNewStudy({
      patientId: patientId,
      studyName: '',
      studyType: '',
      bodyPart: '',
      status: 'In Progress',
      priority: 'Medium',
      consent: false,
      notes: '',
      scheduledDate: '',
      doctorId: ''
    });
    setUploadedFiles([]);
    setIsSubmitting(false);
    setShowAddOffcanvas(true);
  };

  const handleMostRecentAction = (action) => {
    setShowMostRecentDropdown(false);
    switch (action) {
      case 'sendMail':
        console.log('Send mail action');
        break;
      case 'sendMessage':
        console.log('Send message action');
        break;
      case 'downloadReport':
        console.log('Download report action');
        break;
      default:
        break;
    }
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleApplyFilters = () => {
    setShowFilterOffcanvas(false);
  };

  const handleClearFilters = () => {
    setFilters({
      type: '',
      bodyPart: '',
      status: 'All',
      priority: 'All',
      lastUpdate: '',
      startDate: '',
      endDate: ''
    });
  };

  const handleCloseAddOffcanvas = () => {
    setShowAddOffcanvas(false);
    setUploadedFiles([]);
    setIsSubmitting(false);
    // Reset form state
    setNewStudy({
      patientId: patientId,
      studyName: '',
      studyType: '',
      bodyPart: '',
      status: 'In Progress',
      priority: 'Medium',
      consent: false,
      notes: '',
      scheduledDate: '',
      doctorId: ''
    });
    // Ensure body scroll is restored
    setTimeout(() => {
      document.body.style.overflow = 'unset';
    }, 100);
  };

  const handleFormChange = (field, value) => {
    setNewStudy(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + uploadedFiles.length > 5) {
      alert('Maximum 5 files allowed');
      return;
    }
    setUploadedFiles(prev => [...prev, ...files]);
  };

  const removeUploadedFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // FUNCIÓN DE ENVÍO AJUSTADA PARA COINCIDIR CON EL BACKEND
  const handleSubmitNewStudy = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      // Enhanced validation
      const requiredFields = {
        studyName: 'Study Name',
        studyType: 'Study Type',
        patientId: 'Patient ID'
      };

      const missingFields = Object.keys(requiredFields).filter(field => 
        !newStudy[field] || newStudy[field].toString().trim() === ''
      );

      if (missingFields.length > 0) {
        const missingFieldNames = missingFields.map(field => requiredFields[field]);
        throw new Error(`Please fill in the following required fields: ${missingFieldNames.join(', ')}`);
      }

      // Prepare the data for submission - ESTRUCTURA CORREGIDA
      const submissionData = {
        studyName: newStudy.studyName.trim(),
        studyType: newStudy.studyType,
        status: newStudy.status,
        priority: newStudy.priority,
        consent: newStudy.consent,
        patientId: newStudy.patientId || patientId
      };

      // Solo agregar campos opcionales si tienen valores válidos
      if (newStudy.bodyPart && newStudy.bodyPart.trim() !== '') {
        submissionData.bodyPart = newStudy.bodyPart;
      }

      if (newStudy.notes && newStudy.notes.trim() !== '') {
        submissionData.notes = newStudy.notes.trim();
      }

      if (newStudy.scheduledDate && newStudy.scheduledDate.trim() !== '') {
        submissionData.scheduledDate = newStudy.scheduledDate;
      }

      // CORRECCIÓN CRÍTICA: Enviar doctorId solo si está seleccionado
      if (newStudy.doctorId && newStudy.doctorId.trim() !== '') {
        submissionData.doctorId = newStudy.doctorId;
        console.log('Enviando doctorId:', newStudy.doctorId);
      } else {
        console.log('No se enviará doctorId (no seleccionado)');
      }

      console.log('Submitting diagnostic study:', submissionData);
      console.log('Files to upload:', uploadedFiles);
      
      // Create the study using the enhanced service
      const result = await diagnosticImagingService.createDiagnosticImage(submissionData, uploadedFiles);
      console.log('Creation result:', result);
      
      if (result.success) {
        await loadDiagnosticStudies(); // Esto actualizará automáticamente el contador
        handleCloseAddOffcanvas();
        
        // Show success message
        if (window.Swal) {
          window.Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: result.message || 'Diagnostic study created successfully!',
            timer: 3000,
            showConfirmButton: false
          });
        } else {
          alert(result.message || 'Diagnostic study created successfully!');
        }
      } else {
        throw new Error(result.message || 'Failed to create study');
      }
    } catch (error) {
      console.error('Error creating diagnostic study:', error);
      
      // Enhanced error display
      let errorMessage = 'Error creating diagnostic study: ';
      
      if (error.message) {
        errorMessage += error.message;
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage += error.response.data.error;
      } else {
        errorMessage += 'Unknown error occurred';
      }

      // Show detailed error information
      if (window.Swal) {
        window.Swal.fire({
          icon: 'error',
          title: 'Error Creating Study',
          text: errorMessage,
          footer: error.response?.status ? `HTTP Status: ${error.response.status}` : '',
          confirmButtonText: 'OK'
        });
      } else {
        alert(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateStudy = async (studyId, updateData, files = []) => {
    try {
      const result = await diagnosticImagingService.updateDiagnosticImage(studyId, updateData, files);
      if (result.success) {
        await loadDiagnosticStudies(); // Esto actualizará automáticamente el contador
        alert('Study updated successfully!');
      } else {
        throw new Error(result.message || 'Failed to update study');
      }
    } catch (error) {
      console.error('Error updating study:', error);
      alert('Error updating study: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDeleteStudy = async (studyId) => {
    if (window.confirm('Are you sure you want to delete this study?')) {
      try {
        const result = await diagnosticImagingService.deleteDiagnosticImage(studyId);
        if (result.success) {
          await loadDiagnosticStudies(); // Esto actualizará automáticamente el contador
          setShowViewOffcanvas(false);
          alert('Study deleted successfully!');
        } else {
          throw new Error(result.message || 'Failed to delete study');
        }
      } catch (error) {
        console.error('Error deleting study:', error);
        alert('Error deleting study: ' + (error.message || 'Unknown error'));
      }
    }
  };

  const handleStatusUpdate = async (studyId, newStatus) => {
    try {
      const result = await diagnosticImagingService.updateDiagnosticStatus(studyId, newStatus);
      if (result.success) {
        await loadDiagnosticStudies(); // Esto actualizará automáticamente el contador
        alert('Status updated successfully!');
      } else {
        throw new Error(result.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status: ' + (error.message || 'Unknown error'));
    }
  };

  // FUNCIONES DE ESTILO
  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'In Progress':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed':
        return <FiCheck />;
      case 'In Progress':
        return <FiClock />;
      default:
        return <FiSquare />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Urgent':
        return 'danger';
      case 'High':
        return 'warning';
      case 'Medium':
        return 'info';
      case 'Low':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const getActiveFilters = () => {
    const activeFilters = [];
    
    if (globalFilter) {
      activeFilters.push({ key: 'search', label: `Search: "${globalFilter}"` });
    }
    
    if (filters.type) {
      activeFilters.push({ key: 'type', label: `Type: ${filters.type}` });
    }
    
    if (filters.bodyPart) {
      activeFilters.push({ key: 'bodyPart', label: `Body Part: ${filters.bodyPart}` });
    }
    
    if (filters.status !== 'All') {
      activeFilters.push({ key: 'status', label: `Status: ${filters.status}` });
    }
    
    if (filters.priority !== 'All') {
      activeFilters.push({ key: 'priority', label: `Priority: ${filters.priority}` });
    }
    
    if (filters.startDate) {
      activeFilters.push({ key: 'startDate', label: `From: ${new Date(filters.startDate).toLocaleDateString()}` });
    }
    
    if (filters.endDate) {
      activeFilters.push({ key: 'endDate', label: `To: ${new Date(filters.endDate).toLocaleDateString()}` });
    }
    
    if (filters.lastUpdate) {
      activeFilters.push({ key: 'lastUpdate', label: `Last Update: ${new Date(filters.lastUpdate).toLocaleDateString()}` });
    }
    
    return activeFilters;
  };

  const removeFilter = (filterKey) => {
    switch (filterKey) {
      case 'search':
        setGlobalFilter('');
        break;
      case 'type':
        handleFilterChange('type', '');
        break;
      case 'bodyPart':
        handleFilterChange('bodyPart', '');
        break;
      case 'status':
        handleFilterChange('status', 'All');
        break;
      case 'priority':
        handleFilterChange('priority', 'All');
        break;
      case 'startDate':
        handleFilterChange('startDate', '');
        break;
      case 'endDate':
        handleFilterChange('endDate', '');
        break;
      case 'lastUpdate':
        handleFilterChange('lastUpdate', '');
        break;
      default:
        break;
    }
  };

  const getActiveFiltersCount = () => {
    return getActiveFilters().length;
  };

  const handleCloseViewOffcanvas = () => {
    setShowViewOffcanvas(false);
    setSelectedStudy(null);
  };

  const getFileIcon = (fileType) => {
    switch (fileType?.toLowerCase()) {
      case 'pdf':
        return { icon: <FiFileText />, color: 'danger' };
      case 'mp4':
      case 'avi':
      case 'mov':
        return { icon: <FiFile />, color: 'primary' };
      case 'jpg':
      case 'jpeg':
      case 'png':
        return { icon: <FiFile />, color: 'info' };
      default:
        return { icon: <FiFile />, color: 'secondary' };
    }
  };

  const columns = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      size: 200,
      Cell: ({ cell, row }) => (
        <div>
          <strong>{cell.getValue()}</strong>
          <div className="text-muted small">
            {row.original.type}
            {row.original.bodyPart && ` - ${row.original.bodyPart}`}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'lastUpdate',
      header: 'Last Update',
      size: 120,
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 120,
      Cell: ({ cell }) => {
        const status = cell.getValue();
        const color = getStatusColor(status);
        const icon = getStatusIcon(status);
        
        return (
          <span className={`badge bg-${color} d-flex align-items-center gap-1`}>
            <span>{icon}</span>
            {status}
          </span>
        );
      },
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      size: 100,
      Cell: ({ cell }) => {
        const priority = cell.getValue();
        const color = getPriorityColor(priority);
        
        return (
          <span className={`badge bg-${color}`}>
            {priority}
          </span>
        );
      },
    },
    {
      accessorKey: '_id',
      header: 'Action',
      size: 100,
      enableSorting: false,
      Cell: ({ cell }) => (
        <button
          className="btn btn-link btn-sm text-primary p-0 d-flex align-items-center gap-1"
          onClick={(e) => {
            e.stopPropagation();
            handleViewStudy(cell.getValue());
          }}
        >
          <FiEye /> View
        </button>
      ),
    },
    {
      accessorKey: 'consent',
      header: 'Consent',
      size: 80,
      Cell: ({ cell }) => {
        const consent = cell.getValue();
        
        if (consent) {
          return <span className="text-success"><FiCheck /></span>;
        }
        
        return <span className="text-warning"><FiLock /></span>;
      },
    },
  ], []);

  // Mostrar loading si está cargando
  if (isLoading) {
    return (
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Diagnostic Imaging</h6>
        </div>
        <div className="card-body">
          <div className="text-center py-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading diagnostic studies...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white h-100 rounded-10 shadow-sm">

      <h3 className="mb-3" style={{ fontSize: '1.125rem' }}>Diagnostic Imaging</h3>


        {/* Enhanced Search and Actions Bar */}
        <div className="filter-container mb-4">
          <div className="row">

              {/* Search Input */}
              <div className="col-md-5 col-lg-6 d-none d-md-block mb-3 mb-md-0">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name"
                  value={globalFilter}
                  onChange={(e) => handleGlobalFilterChange(e.target.value)}
                />
              </div>

              <div className="col-md-7 col-lg-6 d-flex justify-content-end gap-1 gap-sm-2 flex-wrap">
                {/* Most Recent Dropdown */}
                <div className="dropdown flex-fill flex-grow-sm-0">
                  <button
                    className="btn btn-outline-secondary d-flex gap-2"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMostRecentDropdown(!showMostRecentDropdown);
                    }}
                  >
                    Most Recent <FiChevronDown />
                  </button>
                  
                  {showMostRecentDropdown && (
                    <div className="dropdown-menu show" style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      zIndex: 1000,
                      minWidth: '160px'
                    }}>
                      <button
                        className="dropdown-item d-flex align-items-center gap-2"
                        onClick={() => handleMostRecentAction('sendMail')}
                      >
                        <FiMail /> Send Mail
                      </button>
                      <button
                        className="dropdown-item d-flex align-items-center gap-2"
                        onClick={() => handleMostRecentAction('sendMessage')}
                      >
                        <FiMessageSquare /> Send Message
                      </button>
                      <button
                        className="dropdown-item d-flex align-items-center gap-2"
                        onClick={() => handleMostRecentAction('downloadReport')}
                      >
                        <FiDownload /> Download Report
                      </button>
                    </div>
                  )}
                </div>

                {/* Filter Button */}
                <button
                  className="btn btn-outline-secondary position-relative"
                  onClick={() => setShowFilterOffcanvas(true)}
                >
                  <FiFilter />
                  {getActiveFiltersCount() > 0 && (
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-primary">
                      {getActiveFiltersCount()}
                    </span>
                  )}
                </button>

                {/* Add New Button */}
                <button
                  className="btn btn-primary addnewbtn d-flex gap-2"
                  onClick={handleAddNew}
                >
                  Add
                </button>
              </div>

          </div>
        </div>


        {/* Active Filters Display */}
        {getActiveFilters().length > 0 && (
          <div className="mb-3">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <small className="text-muted">Active filters:</small>
              {getActiveFilters().map((filter) => (
                <span key={filter.key} className="badge bg-light text-dark d-flex align-items-center gap-1">
                  {filter.label}
                  <button
                    type="button"
                    className="btn-close btn-close-sm"
                    style={{fontSize: '0.7em'}}
                    onClick={() => removeFilter(filter.key)}
                  ></button>
                </span>
              ))}
              <button
                className="btn btn-link btn-sm text-decoration-none p-0"
                onClick={handleClearFilters}
              >
                Clear all
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        {isLoading ? (
          <div className="text-center py-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <MaterialReactTable
            columns={columns}
            data={filteredStudies}
            enableRowSelection
            onRowSelectionChange={setRowSelection}
            state={{
              rowSelection,
              globalFilter,
            }}
            onGlobalFilterChange={handleGlobalFilterChange}
            enablePagination
            enableSorting
            enableColumnFilters={false}
            enableGlobalFilter={false}
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => handleViewStudy(row.original._id),
              sx: { cursor: 'pointer' }
            })}
            initialState={{
              pagination: { pageSize: 10 },
              sorting: [{ id: 'lastUpdate', desc: true }]
            }}
          />
        )}
 

      {/* Add New Study Offcanvas */}
      {showAddOffcanvas && (
        <div className="offcanvas offcanvas-end show" style={{visibility: 'visible'}} tabIndex="-1">
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">Add New Diagnostic Study</h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleCloseAddOffcanvas}
            ></button>
          </div>
          <div className="offcanvas-body">
            <form onSubmit={handleSubmitNewStudy}>
              {/* Study Name */}
              <div className="mb-3">
                <label className="form-label">Study Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={newStudy.studyName}
                  onChange={(e) => handleFormChange('studyName', e.target.value)}
                  required
                />
              </div>

              {/* Study Type */}
              <div className="mb-3">
                <label className="form-label">Study Type *</label>
                <select
                  className="form-select"
                  value={newStudy.studyType}
                  onChange={(e) => handleFormChange('studyType', e.target.value)}
                  required
                >
                  <option value="">Select Type</option>
                  {STUDY_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Body Part */}
              <div className="mb-3">
                <label className="form-label">Body Part</label>
                <select
                  className="form-select"
                  value={newStudy.bodyPart}
                  onChange={(e) => handleFormChange('bodyPart', e.target.value)}
                >
                  <option value="">Select Body Part</option>
                  {BODY_PARTS.map(part => (
                    <option key={part} value={part}>{part}</option>
                  ))}
                </select>
              </div>

              {/* Doctor Selection */}
              <div className="mb-3">
                <label className="form-label">Assigned Doctor</label>
                <select
                  className="form-select"
                  value={newStudy.doctorId}
                  onChange={(e) => handleDoctorSelection(e.target.value)}
                >
                  <option value="">Select Doctor</option>
                  {doctors.map(doctor => (
                    <option key={doctor._id} value={doctor._id}>
                      {doctor.name} {doctor.surname}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <div className="mb-3">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={newStudy.status}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                >
                  {STATUSES.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              {/* Priority */}
              <div className="mb-3">
                <label className="form-label">Priority</label>
                <select
                  className="form-select"
                  value={newStudy.priority}
                  onChange={(e) => handleFormChange('priority', e.target.value)}
                >
                  {PRIORITIES.map(priority => (
                    <option key={priority} value={priority}>{priority}</option>
                  ))}
                </select>
              </div>

              {/* Scheduled Date */}
              <div className="mb-3">
                <label className="form-label">Scheduled Date</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={newStudy.scheduledDate}
                  onChange={(e) => handleFormChange('scheduledDate', e.target.value)}
                />
              </div>

              {/* Consent */}
              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="consent"
                    checked={newStudy.consent}
                    onChange={(e) => handleFormChange('consent', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="consent">
                    Patient consent obtained
                  </label>
                </div>
              </div>

              {/* Notes */}
              <div className="mb-3">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={newStudy.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  placeholder="Additional notes or instructions..."
                ></textarea>
              </div>

              {/* File Upload */}
              <div className="mb-3">
                <label className="form-label">Upload Files</label>
                <input
                  type="file"
                  className="form-control"
                  multiple
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={handleFileUpload}
                />
                <div className="form-text">Max 5 files. Accepted: images, PDF, Word documents</div>
              </div>

              {/* Uploaded Files List */}
              {uploadedFiles.length > 0 && (
                <div className="mb-3">
                  <label className="form-label">Uploaded Files:</label>
                  <div className="list-group">
                    {uploadedFiles.map((file, index) => (
                      <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <FiFile />
                          <span>{file.name}</span>
                          <small className="text-muted">({(file.size / 1024).toFixed(1)} KB)</small>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeUploadedFile(index)}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Submit Buttons */}
              <div className="d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-primary flex-fill"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Creating...
                    </>
                  ) : (
                    'Create Study'
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseAddOffcanvas}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filter Offcanvas */}
      {showFilterOffcanvas && (
        <div className="offcanvas offcanvas-end show" style={{visibility: 'visible'}} tabIndex="-1">
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">Filter Studies</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShowFilterOffcanvas(false)}
            ></button>
          </div>
          <div className="offcanvas-body">
            {/* Type Filter */}
            <div className="mb-3">
              <label className="form-label">Study Type</label>
              <select
                className="form-select"
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
              >
                <option value="">All Types</option>
                {STUDY_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Body Part Filter */}
            <div className="mb-3">
              <label className="form-label">Body Part</label>
              <select
                className="form-select"
                value={filters.bodyPart}
                onChange={(e) => handleFilterChange('bodyPart', e.target.value)}
              >
                <option value="">All Body Parts</option>
                {BODY_PARTS.map(part => (
                  <option key={part} value={part}>{part}</option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="All">All Statuses</option>
                {STATUSES.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            {/* Priority Filter */}
            <div className="mb-3">
              <label className="form-label">Priority</label>
              <select
                className="form-select"
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
              >
                <option value="All">All Priorities</option>
                {PRIORITIES.map(priority => (
                  <option key={priority} value={priority}>{priority}</option>
                ))}
              </select>
            </div>

            {/* Date Range Filters */}
            <div className="mb-3">
              <label className="form-label">Date Range</label>
              <div className="row">
                <div className="col-6">
                  <input
                    type="date"
                    className="form-control"
                    placeholder="Start Date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  />
                </div>
                <div className="col-6">
                  <input
                    type="date"
                    className="form-control"
                    placeholder="End Date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Last Update Filter */}
            <div className="mb-3">
              <label className="form-label">Last Update</label>
              <input
                type="date"
                className="form-control"
                value={filters.lastUpdate}
                onChange={(e) => handleFilterChange('lastUpdate', e.target.value)}
              />
            </div>

            {/* Filter Actions */}
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-primary flex-fill"
                onClick={handleApplyFilters}
              >
                Apply Filters
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleClearFilters}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Study Offcanvas */}
      {showViewOffcanvas && selectedStudy && (
        <div className="offcanvas offcanvas-end show" style={{visibility: 'visible', width: '500px'}} tabIndex="-1">
          <div className="offcanvas-header">
            <div>
              <h5 className="offcanvas-title mb-1">Study Details</h5>
              <small className="opacity-75">ID: {selectedStudy._id}</small>
            </div>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleCloseViewOffcanvas}
            ></button>
          </div>
          <div className="offcanvas-body p-0" style={{backgroundColor: '#f8f9fa'}}>
            
            {/* Study Header Card */}
            <div className="card border-0 rounded-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-start justify-content-between mb-3">
                  <div>
                    <h4 className="card-title text-primary mb-1">{selectedStudy.name}</h4>
                    <p className="card-subtitle text-muted mb-2">{selectedStudy.type}</p>
                    {selectedStudy.bodyPart && (
                      <span className="badge bg-light text-dark">
                        <FiTag className="me-1" />
                        {selectedStudy.bodyPart}
                      </span>
                    )}
                  </div>
                  <div className="text-end">
                    <div className="mb-2">
                      <span className={`badge bg-${getStatusColor(selectedStudy.status)} d-flex align-items-center gap-1`}>
                        {getStatusIcon(selectedStudy.status)}
                        {selectedStudy.status}
                      </span>
                    </div>
                    <span className={`badge bg-${getPriorityColor(selectedStudy.priority)}`}>
                      {selectedStudy.priority} Priority
                    </span>
                  </div>
                </div>                
                
              </div>
            </div>

            {/* Study Information Card */}
            <div className="card border-0 shadow-sm mt-3 mx-3">
              <div className="card-header bg-white border-bottom">
                <h6 className="card-title mb-0 d-flex align-items-center">
                  <FiInfo className="me-2 text-primary" />
                  Study Information
                </h6>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="fw-medium text-muted">Study Name</span>
                      <span className="text-dark">{selectedStudy.name}</span>
                    </div>
                  </div>
                  
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="fw-medium text-muted">Study Type</span>
                      <span className="text-dark">{selectedStudy.type}</span>
                    </div>
                  </div>
                  
                  {selectedStudy.bodyPart && (
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                        <span className="fw-medium text-muted">Body Part</span>
                        <span className="text-dark">{selectedStudy.bodyPart}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="fw-medium text-muted">Current Status</span>
                      <span className={`badge bg-${getStatusColor(selectedStudy.status)} d-flex align-items-center gap-1`}>
                        {getStatusIcon(selectedStudy.status)}
                        {selectedStudy.status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="fw-medium text-muted">Priority Level</span>
                      <span className={`badge bg-${getPriorityColor(selectedStudy.priority)}`}>
                        {selectedStudy.priority}
                      </span>
                    </div>
                  </div>
                  
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center py-2">
                      <span className="fw-medium text-muted">Patient Consent</span>
                      {selectedStudy.consent ? (
                        <span className="text-success d-flex align-items-center gap-1">
                          <FiCheck />
                          <span className="fw-medium">Obtained</span>
                        </span>
                      ) : (
                        <span className="text-warning d-flex align-items-center gap-1">
                          <FiLock />
                          <span className="fw-medium">Pending</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline Card */}
            <div className="card border-0 shadow-sm mt-3 mx-3">
              <div className="card-header bg-white border-bottom">
                <h6 className="card-title mb-0 d-flex align-items-center">
                  <FiCalendar className="me-2 text-primary" />
                  Timeline & Dates
                </h6>
              </div>
              <div className="card-body">
                <div className="timeline">
                  <div className="timeline-item d-flex align-items-center mb-3">
                    <div className="timeline-marker bg-primary rounded-circle me-3" style={{width: '12px', height: '12px'}}></div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-medium">Study Created</span>
                        <span className="text-muted small">{formatDateTime(selectedStudy.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {selectedStudy.scheduledDate && (
                    <div className="timeline-item d-flex align-items-center mb-3">
                      <div className="timeline-marker bg-info rounded-circle me-3" style={{width: '12px', height: '12px'}}></div>
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-medium">Scheduled Date</span>
                          <span className="text-muted small">{formatDateTime(selectedStudy.scheduledDate)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="timeline-item d-flex align-items-center">
                    <div className={`timeline-marker bg-${getStatusColor(selectedStudy.status)} rounded-circle me-3`} style={{width: '12px', height: '12px'}}></div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-medium">Last Updated</span>
                        <span className="text-muted small">{formatDateTime(selectedStudy.lastUpdate)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes Card */}
            {selectedStudy.notes && (
              <div className="card border-0 shadow-sm mt-3 mx-3">
                <div className="card-header bg-white border-bottom">
                  <h6 className="card-title mb-0 d-flex align-items-center">
                    <FiFileText className="me-2 text-primary" />
                    Clinical Notes
                  </h6>
                </div>
                <div className="card-body">
                  <div className="bg-light rounded p-3">
                    <p className="mb-0 text-dark" style={{lineHeight: '1.6'}}>
                      {selectedStudy.notes}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Assigned Doctor Card */}
            {selectedStudy.doctorId && (
              <div className="card border-0 shadow-sm mt-3 mx-3">
                <div className="card-header bg-white border-bottom">
                  <h6 className="card-title mb-0 d-flex align-items-center">
                    <FiUser className="me-2 text-primary" />
                    Assigned Doctor
                  </h6>
                </div>
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3" style={{width: '40px', height: '40px'}}>
                      <FiUser className="text-white" />
                    </div>
                    <div>
                      <div className="fw-medium">
                        {(() => {
                          const doctor = doctors.find(d => d._id === selectedStudy.doctorId);
                          return doctor ? `${doctor.name} ${doctor.surname}` : 'Unknown Doctor';
                        })()}
                      </div>
                      <small className="text-muted">Assigned Physician</small>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Files Card */}
            {selectedStudy.files && selectedStudy.files.length > 0 && (
              <div className="card border-0 shadow-sm mt-3 mx-3">
                <div className="card-header bg-white border-bottom">
                  <h6 className="card-title mb-0 d-flex align-items-center">
                    <FiFile className="me-2 text-primary" />
                    Attached Files
                    <span className="badge bg-primary ms-2">{selectedStudy.files.length}</span>
                  </h6>
                </div>
                <div className="card-body p-0">
                  <div className="list-group list-group-flush">
                    {selectedStudy.files.map((file, index) => {
                      const fileInfo = getFileIcon(file.type);
                      return (
                        <div key={index} className="list-group-item d-flex justify-content-between align-items-center py-3">
                          <div className="d-flex align-items-center">
                            <div className={`me-3 d-flex align-items-center justify-content-center rounded bg-light text-${fileInfo.color}`} style={{width: '40px', height: '40px'}}>
                              {fileInfo.icon}
                            </div>
                            <div>
                              <div className="fw-medium">{file.name}</div>
                              <small className="text-muted text-uppercase">{file.type || 'Unknown'} File</small>
                            </div>
                          </div>
                          <div className="d-flex gap-2">
                            <button className="btn btn-sm btn-outline-primary">
                              <FiEye />
                            </button>
                            <button className="btn btn-sm btn-outline-secondary">
                              <FiDownload />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* No Files Message */}
            {(!selectedStudy.files || selectedStudy.files.length === 0) && (
              <div className="card border-0 shadow-sm mt-3 mx-3">
                <div className="card-header bg-white border-bottom">
                  <h6 className="card-title mb-0 d-flex align-items-center">
                    <FiFile className="me-2 text-primary" />
                    Attached Files
                  </h6>
                </div>
                <div className="card-body text-center py-4">
                  <FiFile className="text-muted mb-2" size={32} />
                  <p className="text-muted mb-2">No files attached to this study</p>
                  <button className="btn btn-sm btn-outline-primary">
                    <FiUpload className="me-1" />
                    Upload Files
                  </button>
                </div>
              </div>
            )}

            {/* Study Statistics Card */}
            <div className="card border-0 shadow-sm mt-3 mx-3 mb-3">
              <div className="card-header bg-white border-bottom">
                <h6 className="card-title mb-0 d-flex align-items-center">
                  <FiInfo className="me-2 text-primary" />
                  Study Statistics
                </h6>
              </div>
              <div className="card-body">
                <div className="row text-center">
                  <div className="col-4">
                    <div className="border-end">
                      <div className="h5 mb-1 text-primary">{selectedStudy.files ? selectedStudy.files.length : 0}</div>
                      <small className="text-muted">Files</small>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="border-end">
                      <div className="h5 mb-1 text-success">
                        {(() => {
                          const created = new Date(selectedStudy.createdAt);
                          const now = new Date();
                          const diffTime = Math.abs(now - created);
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                          return diffDays;
                        })()}
                      </div>
                      <small className="text-muted">Days Old</small>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="h5 mb-1 text-warning">
                      {selectedStudy.consent ? '✓' : '⚠️'}
                    </div>
                    <small className="text-muted">Consent</small>
                  </div>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      )}

      {/* Backdrop for offcanvas */}
      {(showAddOffcanvas || showViewOffcanvas || showFilterOffcanvas) && (
        <div 
          className="offcanvas-backdrop fade show"
          onClick={() => {
            setShowAddOffcanvas(false);
            setShowViewOffcanvas(false);
            setShowFilterOffcanvas(false);
          }}
        ></div>
      )}
    </div>
  );
};

export default DiagnosticImagingTreatments;